import { copiesOpponent, duelForm, duelHitPoints } from '../game/battle';
import { cardBattleChances } from '../game/cardBattle';
import { strategicWinProbability as attackerWinProbability } from '../game/combatOdds';
import { applyBattleResult } from '../game/game';
import { pieceById } from '../game/movement';
import { rollWinShares } from '../game/rollBattle';
import type { GameContext, GameState, PieceId } from '../game/types';
import type { AiOptions } from './evaluate';

/** A participant's chance of winning the pending fight, and the damage it would carry out. */
export interface FightWinner {
  pieceId: PieceId;
  p: number;
  winnerDamage: number;
}

/**
 * Every participant of the pending fight as its possible winner, in participant order, by the
 * public odds of the game's combat mode: Card's support-neutral prior, or else the roll's share of
 * the top draw at the seats' own levels (`rollWinShares`). Each winner is wounded by the likeliest
 * of the others to have pressed it, as a clash takes its wounds from the runner-up.
 */
export function fightWinners(
  ctx: GameContext,
  state: GameState,
  model?: AiOptions['survivorModel'],
): FightWinner[] {
  const battle = state.pendingBattle!;
  const likeliestRival = (odds: readonly { pieceId: PieceId; p: number }[], winner: PieceId) =>
    pieceById(
      state,
      odds
        .filter((other) => other.pieceId !== winner)
        .sort((a, b) => b.p - a.p || a.pieceId - b.pieceId)[0]!.pieceId,
    )!;
  if (ctx.combatMode === 'card') {
    const odds = cardBattleChances(ctx, state, {}).map((score) => ({
      pieceId: score.pieceId,
      p: score.probability!,
    }));
    return odds.map(({ pieceId, p }) => {
      const winner = pieceById(state, pieceId)!;
      const rival = likeliestRival(odds, pieceId);
      return {
        pieceId,
        p,
        winnerDamage: predictedWinnerDamage(ctx, state, winner, rival, model),
      };
    });
  }
  const ids = battle.participantIds ?? [battle.attackerId, battle.defenderId];
  const shares = rollWinShares(ctx, state, { levels: ctx.cardLevels });
  const odds = ids.map((pieceId, i) => ({ pieceId, p: shares[i]! }));
  return odds.map(({ pieceId, p }) => ({
    pieceId,
    p,
    winnerDamage: predictedWinnerDamage(
      ctx,
      state,
      pieceById(state, pieceId)!,
      likeliestRival(odds, pieceId),
      model,
    ),
  }));
}

/**
 * The outcomes the board AI weighs for a pending fight of three or more (a muster): every
 * participant can win it and carries its wounds onto the square. A fight of two is the search's
 * own two-outcome chance node.
 */
export function multiwayOutcomes(
  ctx: GameContext,
  state: GameState,
  model?: AiOptions['survivorModel'],
) {
  return ctx.combatMode === 'card'
    ? cardSearchOutcomes(ctx, state)
    : rollSearchOutcomes(ctx, state, model);
}

/** A Roll clash of three or more, as `fightWinners` reads it. */
export function rollSearchOutcomes(
  ctx: GameContext,
  state: GameState,
  model?: AiOptions['survivorModel'],
) {
  return fightWinners(ctx, state, model)
    .filter((winner) => winner.p > 0)
    .map((winner) => outcome(ctx, state, winner));
}

/** Public, support-neutral multi-way prior. Every participant can survive, not just the first two. */
export function cardSearchOutcomes(ctx: GameContext, state: GameState) {
  return fightWinners(ctx, state).map((winner) => outcome(ctx, state, winner));
}

function outcome(ctx: GameContext, state: GameState, winner: FightWinner) {
  return {
    p: winner.p,
    next: applyBattleResult(ctx, state, {
      winnerId: winner.pieceId,
      winnerDamage: winner.winnerDamage,
    }),
  };
}

/** Expected damage the winner carries out of a fight: a share of the loser's strength. */
export function predictedWinnerDamage(
  ctx: GameContext,
  state: GameState,
  winner: { id: number; damage: number; character: string },
  loser: { id: number; character: string },
  model?: AiOptions['survivorModel'],
): number {
  const battle = state.pendingBattle!;
  const winnerPiece = pieceById(state, winner.id)!;
  const loserPiece = pieceById(state, loser.id)!;
  // A shapeshifter fights as a copy of its opponent and is wounded in the copied body; the board
  // keeps the same share of its own (D-56).
  const winnerHp = duelHitPoints(ctx, state, winnerPiece, loserPiece, battle.x, battle.y);
  const toOwn = copiesOpponent(ctx, winnerPiece, loserPiece)
    ? ctx.roster[winner.character]!.hitPoints / ctx.roster[loser.character]!.hitPoints
    : 1;
  const loserStrength =
    duelHitPoints(ctx, state, loserPiece, winnerPiece, battle.x, battle.y) *
    (ctx.roster[duelForm(ctx, loserPiece, winnerPiece)]?.attack.damage ?? 1);
  const winnerStrength =
    winnerHp * (ctx.roster[duelForm(ctx, winnerPiece, loserPiece)]?.attack.damage ?? 1);
  const closeness = Math.min(1, loserStrength / winnerStrength);
  // winnerDamage is cumulative; cap only the NEW wounds by remaining battle health.
  // Capping the total by remaining health made a badly wounded survivor appear healed.
  const wounds = Math.min(winnerHp - 1, Math.round(winnerHp * closeness * 0.55));
  if (model !== 'conditional-expert' || ctx.combatMode !== 'arena')
    return winner.damage + (toOwn === 1 ? wounds : Math.round(wounds * toOwn));
  // Frozen six-feature fit from 13,612 training survivors; independently checked
  // on reserved blocks and 18,240 fresh health-condition fights. No faction or
  // character identity coefficients. This changes search predictions, not fights.
  const p = attackerWinProbability(ctx, state);
  const winnerProbability = winner.id === battle.attackerId ? p : 1 - p;
  const winnerType = ctx.roster[duelForm(ctx, winnerPiece, loserPiece)]?.attack.type;
  const loserType = ctx.roster[duelForm(ctx, loserPiece, winnerPiece)]?.attack.type;
  const fraction =
    0.34323708862405694 +
    0.37062334994352497 * (1 - winnerProbability) -
    0.2486034610542372 * (wounds / winnerHp) +
    0.08120715014045077 * +(winnerType === 'melee') +
    0.1336000513546979 * +(winnerType === 'burn') -
    0.08709908786794805 * +(loserType === 'melee');
  return winner.damage + Math.max(0, Math.min(winnerHp - 1, fraction * winnerHp)) * toOwn;
}
